import { errorWords } from "@alm/domain";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import type { FacilitySummary } from "@alm/api-contracts";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { RemoveConfirm } from "@/components/app/fields/remove-confirm";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { orpc } from "@/lib/orpc";
import { useCanWithoutBuilding } from "@/lib/policy";
import { FacilityForm, facilityFormToInput } from "./-components/facility-form";
import { FacilityTable } from "./-components/facility-table";

export const Route = createFileRoute("/_authenticated/_administration/settings/facilities/")({
  component: FacilitiesPage,
});

/** The organization's buildings: which it runs, and a new one. Each building's own settings are
 * under Facility settings. */
function FacilitiesPage() {
  const [creating, setCreating] = useState(false);
  const [deactivating, setDeactivating] = useState<FacilitySummary | null>(null);
  const canCreate = useCanWithoutBuilding("facility:create");
  const queryClient = useQueryClient();
  const list = useQuery(
    orpc.administration.facilities.list.queryOptions({ input: { includeInactive: true } }),
  );
  const create = useMutation(
    orpc.administration.facilities.create.mutationOptions({
      onSuccess: async () => {
        toast.success("Facility added");
        setCreating(false);
        await queryClient.invalidateQueries({ queryKey: orpc.administration.facilities.key() });
      },
      onError: (error) => toast.error(errorWords(error)),
    }),
  );
  const setStatus = useMutation(
    orpc.administration.facilities.update.mutationOptions({
      onSuccess: async (_, input) => {
        toast.success(input.status === "active" ? "Facility reactivated" : "Facility deactivated");
        await queryClient.invalidateQueries({ queryKey: orpc.administration.facilities.key() });
      },
      onError: (error) => toast.error(errorWords(error)),
    }),
  );
  const changeStatus = (facility: FacilitySummary, status: "active" | "inactive") =>
    status === "inactive"
      ? setDeactivating(facility)
      : setStatus.mutate({ facilityId: facility.id, status });
  return (
    <>
      <PageHeader
        title="Facilities"
        count={list.data?.length}
        meta="Each building you run. Opening one shows its profile under Facility settings."
        actions={canCreate ? <Button onClick={() => setCreating(true)}>Add facility</Button> : null}
      />
      {list.isPending ? (
        <Skeleton className="h-64" />
      ) : list.isError ? (
        <ReadFailed
          message="Could not load facilities."
          error={list.error}
          retrying={list.isFetching}
          onRetry={() => void list.refetch()}
        />
      ) : list.data.length === 0 ? (
        <EmptyState
          title="No facilities yet"
          description="Buildings you add appear here, ready for admitting residents and scheduling staff."
        />
      ) : (
        <FacilityTable rows={list.data} onChangeStatus={changeStatus} />
      )}
      {deactivating ? (
        <RemoveConfirm
          title={`Deactivate ${deactivating.name}?`}
          description="It leaves the building switcher, the nightly jobs and its careers page. Its residents, staff and records stay, and you can reactivate it from this list."
          confirmLabel="Deactivate facility"
          onConfirm={() => setStatus.mutate({ facilityId: deactivating.id, status: "inactive" })}
          onClose={() => setDeactivating(null)}
        />
      ) : null}
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add facility</DialogTitle>
          </DialogHeader>
          <FacilityForm
            pending={create.isPending}
            submitLabel="Add facility"
            onCancel={() => setCreating(false)}
            onSubmit={(values) => create.mutate(facilityFormToInput(values))}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
